import { Result } from "../errors";

// # Scalar Name
//
// A validated string type, checked for validity as a name for a signal, instance, or similar.
//
export class ScalarName {
  readonly nameKind = "scalar";
  private constructor(readonly src: string) {}

  static parse(src: string): Result<ScalarName> {
    //
    // Format:
    // - Start with `[a-zA-Z]`, i.e. an alpha character
    // - Follow with zero or more alphanumerics (`[a-zA-Z0-9]`), underscores, and/or some_weirder stuff.
    //
    // Similar to (fairly simple) identifier rules in many programming languages.
    // But with exclamation points! And plus and minus! And... ok maybe not normal program identifiers.
    //
    // NOTE there is almost certainly a JavaScript way to make this one regex comparison, we just don't know it.
    //
    if (!src.length) {
      return Result.Err("Empty name");
    }
    const [first, ...rest] = src;
    if (!first.match(/[a-zA-Z]/))
      return Result.Err({ msg: "Invalid name", src });
    for (const char of rest) {
      if (!char.match(/[a-zA-Z0-9_!+-]/))
        return Result.Err({ msg: "Invalid name", src });
    }
    return Result.Ok(new ScalarName(src));
  }

  // String representation
  repr(): string {
    return this.src;
  }

  // Width in bits
  width(): number {
    return 1;
  }

  base(): ScalarName {
    return this;
  }
}

// # Bus or Slice Name
//
// Consists of a `ScalarName`, plus a primary `left` index and optional `right` index.
// Constructed `BusName`s are *always* valid, including validation that the `left` and `right` indices are valid.
//
export class BusName {
  readonly nameKind = "bus";
  private constructor(
    readonly scalar: ScalarName,
    readonly left: number,
    readonly right?: number
  ) {}

  // Format: `sigName<1>` or `sigName<2:0>`
  static parse(src: string): Result<BusName> {
    const parts = src.split("<");
    if (parts.length !== 2) {
      const msg = `Invalid bus ${src}`;
      return Result.Err(msg);
    }
    const [leftSide, rightSide] = parts;

    const signalNameResult = ScalarName.parse(leftSide);
    if (signalNameResult.err) return signalNameResult;
    const signalName = signalNameResult.val;

    if (!rightSide.endsWith(">")) {
      const msg = `Invalid bus ${src}`;
      return Result.Err(msg);
    }
    const indexSrc = rightSide.slice(0, -1);
    const indexParts = indexSrc.split(":");
    if (indexParts.length < 1 || indexParts.length > 2) {
      const msg = `Invalid bus ${src}`;
      return Result.Err(msg);
    }
    // Length is either 1 (scalar) or 2 (range).
    //  the left index either way.
    const left = parseInt(indexParts[0]);
    if (isNaN(left)) {
      const msg = `Invalid bus ${src}`;
      return Result.Err(msg);
    }
    if (indexParts.length === 1) {
      // Scalar.
      return Result.Ok(new BusName(signalName, left));
    }
    // Range.  the right side.
    const right = parseInt(indexParts[1]);
    if (isNaN(right)) {
      const msg = `Invalid bus ${src}`;
      return Result.Err(msg);
    }
    return Result.Ok(new BusName(signalName, left, right));
  }

  // String representation
  repr(): string {
    // Format:
    //  `sigName<1>` or `sigName<2:0>`
    const { scalar, left, right } = this;
    const sname = scalar.repr();
    if (right === undefined) {
      return `${sname}<${left}>`;
    }
    return `${sname}<${left}:${right}>`;
  }

  // Width in bits
  width(): number {
    if (this.right === undefined) return 1; // Single-bit slice
    const min = Math.min(this.left, this.right);
    const max = Math.max(this.left, this.right);
    return 1 + max - min;
  }

  base(): ScalarName {
    return this.scalar;
  }
}

// # Base or Bus Defining Name
// Can be a `BaseName`, or can be a `BusName` with a right index of zero.
export type ScalarOrBusName = ScalarName | BusName;

export const ScalarOrBusName = {
  parse: (src: string): Result<ScalarOrBusName> => {
    if (src.includes("<")) {
      // Parse this as a bus name
      return BusName.parse(src);
    }
    return ScalarName.parse(src);
  },
};
